import React, { useState, useEffect, useCallback } from 'react';
import api from '../api';
import { useAuth } from '../App';
import TeamManagement from '../components/TeamManagement';

const Organization = () => {
  const { user } = useAuth();

  // Org core
  const [org, setOrg] = useState(null);
  const [orgMembers, setOrgMembers] = useState([]);
  const [orgRole, setOrgRole] = useState(null);
  const [orgInvites, setOrgInvites] = useState([]);
  const [loading, setLoading] = useState(true);

  // Invite
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('member');
  const [inviting, setInviting] = useState(false);
  const [inviteLink, setInviteLink] = useState('');

  // Org settings
  const [editingName, setEditingName] = useState(false);
  const [orgNameDraft, setOrgNameDraft] = useState('');

  // Challenges — removed (replaced by Answer/Screenshot Library)

  // Org tags
  const [orgTags, setOrgTags] = useState([]);
  const [newTagName, setNewTagName] = useState('');
  const [newTagColor, setNewTagColor] = useState('#6B7280');

  const isAdmin = orgRole === 'admin';

  // ── Data Fetching ──
  const fetchOrg = useCallback(async () => {
    try {
      const [orgRes, invitesRes] = await Promise.all([
        api.get('/org'),
        api.get('/org/invites').catch(() => ({ data: { invites: [] } })),
      ]);
      setOrg(orgRes.data.org);
      setOrgMembers(orgRes.data.members || []);
      setOrgRole(orgRes.data.role);
      setOrgInvites(invitesRes.data.invites || []);
      setOrgNameDraft(orgRes.data.org?.name || '');
    } catch (e) {
      console.error('Org fetch error:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchOrgTags = useCallback(async () => {
    try {
      const res = await api.get('/tags');
      setOrgTags((res.data.tags || []).filter(t => t.scope === 'org'));
    } catch (e) { /* non-fatal */ }
  }, []);

  useEffect(() => {
    fetchOrg();
    fetchOrgTags();
  }, [fetchOrg, fetchOrgTags]);

  // ── Actions ──
  const sendInvite = async () => {
    if (!inviteEmail) return;
    setInviting(true);
    try {
      const res = await api.post('/org/invite', { email: inviteEmail, role: inviteRole });
      const token = res.data.invite?.token;
      if (token) setInviteLink(`${window.location.origin}/invite/${token}`);
      setInviteEmail('');
      fetchOrg();
    } catch (e) {
      alert(e.response?.data?.error || 'Failed to send invite');
    } finally { setInviting(false); }
  };

  const cancelInvite = async (id) => {
    try { await api.delete(`/org/invite/${id}`); fetchOrg(); } catch (e) { alert('Failed to cancel invite'); }
  };

  const changeRole = async (userId, newRole) => {
    try { await api.put(`/org/member/${userId}/role`, { role: newRole }); fetchOrg(); }
    catch (e) { alert(e.response?.data?.error || 'Failed to change role'); }
  };

  const removeMember = async (userId, name) => {
    if (!window.confirm(`Remove ${name} from the organization?`)) return;
    try { await api.delete(`/org/member/${userId}`); fetchOrg(); }
    catch (e) { alert(e.response?.data?.error || 'Failed to remove member'); }
  };

  const resetPassword = async (userId, name) => {
    if (!window.confirm(`Reset password for ${name}? A temporary password will be generated.`)) return;
    try {
      const res = await api.post(`/org/member/${userId}/reset-password`);
      const tempPw = res.data.temp_password;
      const copied = await navigator.clipboard.writeText(tempPw).then(() => true).catch(() => false);
      alert(`Temporary password for ${name}:\n\n${tempPw}\n\n${copied ? '✓ Copied to clipboard!' : 'Copy it now — it won\'t be shown again.'}\n\nShare this securely. They should change it after logging in.`);
    } catch (e) {
      alert(e.response?.data?.error || 'Failed to reset password');
    }
  };

  const resetInvitePassword = async (inviteId, email) => {
    if (!window.confirm(`Reset password for ${email}? This only works if they already have an account.`)) return;
    try {
      const res = await api.post(`/org/invite/${inviteId}/reset-password`);
      const tempPw = res.data.temp_password;
      const copied = await navigator.clipboard.writeText(tempPw).then(() => true).catch(() => false);
      alert(`Temporary password for ${email}:\n\n${tempPw}\n\n${copied ? '✓ Copied to clipboard!' : 'Copy it now — it won\'t be shown again.'}\n\n${res.data.message}`);
    } catch (e) {
      alert(e.response?.data?.error || 'Failed to reset password');
    }
  };

  const renameOrg = async () => {
    if (!orgNameDraft.trim()) return;
    try {
      await api.put('/org', { name: orgNameDraft.trim() });
      setEditingName(false);
      fetchOrg();
    } catch (e) { alert('Failed to rename organization'); }
  };

  const createOrgTag = async () => {
    if (!newTagName.trim()) return;
    try {
      await api.post('/tags', { name: newTagName, color: newTagColor, scope: 'org' });
      setNewTagName('');
      setNewTagColor('#6B7280');
      fetchOrgTags();
    } catch (e) { alert(e.response?.data?.error || 'Failed to create tag'); }
  };

  const deleteOrgTag = async (id) => {
    if (!window.confirm('Delete this org tag?')) return;
    try { await api.delete(`/tags/${id}`); fetchOrgTags(); }
    catch (e) { alert('Failed to delete tag'); }
  };

  // ── Loading / No Org States ──
  if (loading) return <div className="p-8 text-zinc-500">Loading...</div>;

  if (!org) return (
    <div className="p-8 max-w-lg mx-auto mt-20">
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-8 text-center">
        <div className="text-3xl mb-3">🏢</div>
        <h2 className="text-xl font-bold text-white mb-2">No Organization Yet</h2>
        <p className="text-zinc-400 text-sm mb-6">
          You're not part of an organization. Ask your admin for an invite link, or contact support to create one.
        </p>
      </div>
    </div>
  );

  // ── Render ──
  return (
    <div className="p-6 lg:p-8 max-w-[1100px] mx-auto">

      {/* ── HEADER ── */}
      <div className="flex items-center justify-between mb-8">
        <div>
          {editingName && isAdmin ? (
            <div className="flex items-center gap-2">
              <input
                type="text" value={orgNameDraft} onChange={e => setOrgNameDraft(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && renameOrg()}
                className="bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-xl font-bold text-white focus:outline-none focus:border-[#1EE4BC]/50"
                autoFocus
              />
              <button onClick={renameOrg} className="text-sm text-[#1EE4BC] hover:text-[#1EE4BC]/80">Save</button>
              <button onClick={() => { setEditingName(false); setOrgNameDraft(org.name); }} className="text-sm text-zinc-500">Cancel</button>
            </div>
          ) : (
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-white">{org.name}</h1>
              {isAdmin && (
                <button onClick={() => setEditingName(true)} className="text-zinc-600 hover:text-zinc-400 text-xs">✏️</button>
              )}
            </div>
          )}
          <p className="text-zinc-500 mt-1 text-sm">
            {orgMembers.length} member{orgMembers.length !== 1 ? 's' : ''}
            {isAdmin && <span className="ml-2 text-[#1EE4BC]/60">· Admin</span>}
            {!isAdmin && <span className="ml-2 text-zinc-600">· Member</span>}
          </p>
        </div>
        {isAdmin && (
          <button
            onClick={() => { setShowInviteModal(true); setInviteLink(''); }}
            className="bg-[#1EE4BC] text-black px-5 py-2.5 rounded-lg text-sm font-semibold hover:opacity-90 transition-opacity"
          >
            + Invite Member
          </button>
        )}
      </div>

      <div className="grid grid-cols-3 gap-6">

        {/* ──────────────────────────────────────────── */}
        {/* LEFT COLUMN (2 cols) — Members + Challenges  */}
        {/* ──────────────────────────────────────────── */}
        <div className="col-span-2 space-y-6">

          {/* TEAM DIRECTORY */}
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
            <div className="px-5 py-4 border-b border-zinc-800 flex items-center justify-between">
              <h2 className="text-xs text-zinc-500 uppercase tracking-wider font-medium">Team Directory</h2>
              <span className="text-xs text-zinc-600">{orgMembers.length} members</span>
            </div>
            <div className="divide-y divide-zinc-800/50">
              {orgMembers.map(m => {
                return (
                  <div key={m.user_id} className={`px-5 py-4 flex items-center gap-4 ${m.user_id === user?.id ? 'bg-[#1EE4BC]/[0.02]' : ''}`}>
                    {/* Avatar */}
                    <div className="w-10 h-10 rounded-full bg-zinc-800 flex items-center justify-center text-sm font-bold text-white flex-shrink-0 border border-zinc-700">
                      {m.name?.[0]?.toUpperCase() || '?'}
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-white text-sm font-medium">{m.name}</span>
                        {m.user_id === user?.id && <span className="text-[9px] text-zinc-600">(you)</span>}
                        <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                          m.role === 'admin' ? 'bg-[#1EE4BC]/10 text-[#1EE4BC] border border-[#1EE4BC]/20' : 'bg-zinc-800 text-zinc-500'
                        }`}>
                          {m.role}
                        </span>
                      </div>
                      <div className="text-xs text-zinc-500 truncate">{m.email}</div>
                    </div>

                    {/* Status indicators */}
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {m.gmail_connected && (
                        <span className="text-[10px] text-green-400 bg-green-400/10 px-1.5 py-0.5 rounded">Gmail</span>
                      )}
                      {!m.gmail_connected && (
                        <span className="text-[10px] text-zinc-600 bg-zinc-800 px-1.5 py-0.5 rounded">No Gmail</span>
                      )}
                    </div>

                    {/* Admin actions */}
                    {isAdmin && m.user_id !== user?.id && (
                      <div className="flex items-center gap-1 flex-shrink-0 ml-2">
                        <button
                          onClick={() => changeRole(m.user_id, m.role === 'admin' ? 'member' : 'admin')}
                          className="text-[10px] text-zinc-500 hover:text-white px-2 py-1 rounded hover:bg-zinc-800 transition-colors"
                          title={m.role === 'admin' ? 'Demote to member' : 'Promote to admin'}
                        >
                          {m.role === 'admin' ? '↓ Demote' : '↑ Promote'}
                        </button>
                        <button
                          onClick={() => resetPassword(m.user_id, m.name)}
                          className="text-[10px] text-amber-400/60 hover:text-amber-400 px-2 py-1 rounded hover:bg-amber-500/10 transition-colors"
                          title="Reset password"
                        >
                          Reset PW
                        </button>
                        <button
                          onClick={() => removeMember(m.user_id, m.name)}
                          className="text-[10px] text-red-400/60 hover:text-red-400 px-2 py-1 rounded hover:bg-red-500/10 transition-colors"
                        >
                          Remove
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* PENDING INVITES (admin only) */}
          {isAdmin && orgInvites.length > 0 && (
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
              <div className="px-5 py-4 border-b border-zinc-800">
                <h2 className="text-xs text-zinc-500 uppercase tracking-wider font-medium">Pending Invites</h2>
              </div>
              <div className="divide-y divide-zinc-800/50">
                {orgInvites.map(inv => (
                  <div key={inv.id} className="px-5 py-3 flex items-center justify-between">
                    <div>
                      <span className="text-sm text-zinc-300">{inv.email}</span>
                      <span className={`text-[10px] ml-2 px-1.5 py-0.5 rounded ${
                        inv.role === 'admin' ? 'bg-[#1EE4BC]/10 text-[#1EE4BC]' : 'bg-zinc-800 text-zinc-500'
                      }`}>{inv.role}</span>
                      {new Date(inv.expires_at) < new Date() && (
                        <span className="text-[10px] text-red-400 ml-2">expired</span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <button 
                        onClick={() => resetInvitePassword(inv.id, inv.email)} 
                        className="text-xs text-amber-400/60 hover:text-amber-400 transition-colors"
                        title="Reset password for this user's existing account"
                      >
                        Reset PW
                      </button>
                      <button onClick={() => cancelInvite(inv.id)} className="text-xs text-red-400/60 hover:text-red-400 transition-colors">Cancel</button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* TEAM MANAGEMENT (admin only) */}
          {isAdmin && (
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden p-6">
              <TeamManagement user={user} />
            </div>
          )}

          {/* ANSWER LIBRARY */}
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
            <div className="px-5 py-4 border-b border-zinc-800">
              <h2 className="text-xs text-zinc-500 uppercase tracking-wider font-medium">Answer Library</h2>
              <p className="text-xs text-zinc-600 mt-1">Standard answers to agency RFP questions. Auto-fills when generating media plans.</p>
            </div>
            <div className="p-5">
              <AnswerLibrarySection />
            </div>
          </div>

          {/* SCREENSHOT LIBRARY */}
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
            <div className="px-5 py-4 border-b border-zinc-800">
              <h2 className="text-xs text-zinc-500 uppercase tracking-wider font-medium">Screenshot Library</h2>
              <p className="text-xs text-zinc-600 mt-1">Ad unit screenshots used in media plans and agency template submissions.</p>
            </div>
            <div className="p-5">
              <ScreenshotLibrarySection />
            </div>
          </div>
        </div>

        {/* ──────────────────────────────────────────── */}
        {/* RIGHT COLUMN — Stats + Tags + Quick Actions  */}
        {/* ──────────────────────────────────────────── */}
        <div className="space-y-6">

          {/* ORG TAGS (admin can manage) */}
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xs text-zinc-500 uppercase tracking-wider font-medium">Org Tags</h3>
              <span className="text-[10px] text-zinc-600">{orgTags.length} tags</span>
            </div>
            <div className="space-y-1.5">
              {orgTags.map(tag => (
                <div key={tag.id} className="flex items-center justify-between py-1.5 px-2 rounded hover:bg-zinc-800/50">
                  <div className="flex items-center gap-2">
                    <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: tag.color }} />
                    <span className="text-xs text-white">{tag.name}</span>
                    <span className="text-[9px] text-zinc-600">{tag.usage_count || 0}</span>
                  </div>
                  {isAdmin && (
                    <button onClick={() => deleteOrgTag(tag.id)} className="text-[10px] text-zinc-600 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-all">×</button>
                  )}
                </div>
              ))}
              {orgTags.length === 0 && <div className="text-[10px] text-zinc-600 py-2">No org-wide tags yet</div>}
            </div>
            {isAdmin && (
              <div className="flex items-center gap-2 mt-3 pt-3 border-t border-zinc-800">
                <input
                  type="color" value={newTagColor} onChange={e => setNewTagColor(e.target.value)}
                  className="w-6 h-6 rounded border-none cursor-pointer bg-transparent flex-shrink-0"
                />
                <input
                  type="text" placeholder="New tag..." value={newTagName}
                  onChange={e => setNewTagName(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && createOrgTag()}
                  className="flex-1 bg-transparent text-white text-xs placeholder-zinc-600 focus:outline-none"
                />
                <button onClick={createOrgTag} disabled={!newTagName.trim()} className="text-[10px] text-[#1EE4BC] disabled:text-zinc-700">Add</button>
              </div>
            )}
          </div>

          {/* MEMBERSHIP INFO */}
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5">
            <h3 className="text-xs text-zinc-500 uppercase tracking-wider font-medium mb-3">Your Membership</h3>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-xs text-zinc-500">Role</span>
                <span className={`text-xs font-medium ${isAdmin ? 'text-[#1EE4BC]' : 'text-white'}`}>{orgRole}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-xs text-zinc-500">Organization</span>
                <span className="text-xs text-white">{org.name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-xs text-zinc-500">Team Size</span>
                <span className="text-xs text-white">{orgMembers.length}</span>
              </div>
            </div>
            {!isAdmin && (
              <button className="w-full mt-4 py-2 border border-red-500/20 text-red-400/60 text-xs rounded-lg hover:bg-red-500/5 hover:text-red-400 transition-colors">
                Leave Organization
              </button>
            )}
          </div>
        </div>
      </div>

      {/* ── INVITE MODAL ── */}
      {showInviteModal && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setShowInviteModal(false)}>
          <div className="bg-zinc-900 border border-zinc-700 rounded-xl p-6 w-full max-w-md" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-white mb-4">Invite Team Member</h3>

            {inviteLink ? (
              <div>
                <div className="text-sm text-green-400 mb-2">Invite created! Share this link:</div>
                <div className="flex items-center gap-2">
                  <input type="text" readOnly value={inviteLink}
                    className="flex-1 bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-xs text-zinc-300 font-mono" />
                  <button onClick={() => navigator.clipboard.writeText(inviteLink)}
                    className="px-3 py-2 bg-[#1EE4BC] text-black rounded-lg text-sm font-medium">Copy</button>
                </div>
                <p className="text-xs text-zinc-500 mt-2">This link expires in 7 days.</p>
                <button onClick={() => setShowInviteModal(false)}
                  className="mt-4 w-full py-2 bg-zinc-800 text-white rounded-lg text-sm font-medium">Done</button>
              </div>
            ) : (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm text-zinc-400 mb-1">Email Address</label>
                  <input type="email" value={inviteEmail} onChange={e => setInviteEmail(e.target.value)}
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-white placeholder-zinc-500 focus:outline-none focus:border-[#1EE4BC]"
                    placeholder="teammate@company.com" />
                </div>
                <div>
                  <label className="block text-sm text-zinc-400 mb-1">Role</label>
                  <select value={inviteRole} onChange={e => setInviteRole(e.target.value)}
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-[#1EE4BC]">
                    <option value="member">Member</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
                <div className="flex gap-3">
                  <button onClick={() => setShowInviteModal(false)}
                    className="flex-1 py-2.5 bg-zinc-800 text-white rounded-lg text-sm font-medium">Cancel</button>
                  <button onClick={sendInvite} disabled={inviting || !inviteEmail}
                    className="flex-1 py-2.5 bg-[#1EE4BC] text-black rounded-lg text-sm font-semibold disabled:opacity-50">
                    {inviting ? 'Sending...' : 'Create Invite'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Organization;

// ============================================================
// ANSWER LIBRARY SECTION
// ============================================================

const ANSWER_CATEGORIES = ['Publisher Info', 'Targeting', 'Ad Serving', 'Brand Safety', 'Measurement', 'Technical', 'Other'];

const AnswerLibrarySection = () => {
  const [answers, setAnswers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ question_text: '', answer: '', category: 'Publisher Info' });
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');

  useEffect(() => { fetchAnswers(); }, []);

  const fetchAnswers = async () => {
    try { const res = await api.get('/org/answer-library'); setAnswers(res.data.answers || []); }
    catch (e) {} finally { setLoading(false); }
  };

  const hashQuestion = (text) => {
    let h = 0;
    for (let i = 0; i < text.length; i++) { h = ((h << 5) - h) + text.charCodeAt(i); h |= 0; }
    return Math.abs(h).toString(16).padStart(8, '0');
  };

  const saveAnswer = async (isNew = false) => {
    if (!form.question_text.trim() || !form.answer.trim()) return;
    setSaving(true);
    try {
      const hash = isNew ? hashQuestion(form.question_text) : (answers.find(a => a.id === editingId)?.question_hash || hashQuestion(form.question_text));
      await api.put('/org/answer-library', { question_hash: hash, question_text: form.question_text, answer: form.answer, category: form.category });
      await fetchAnswers();
      setEditingId(null); setShowAdd(false);
      setForm({ question_text: '', answer: '', category: 'Publisher Info' });
    } catch (e) { alert('Failed to save'); } finally { setSaving(false); }
  };

  const deleteAnswer = async (id) => {
    if (!window.confirm('Delete this answer?')) return;
    try { await api.delete(`/org/answer-library/${id}`); setAnswers(prev => prev.filter(a => a.id !== id)); }
    catch (e) { alert('Failed to delete'); }
  };

  const startEdit = (a) => { setEditingId(a.id); setShowAdd(false); setForm({ question_text: a.question_text, answer: a.answer, category: a.category || 'Other' }); };
  const cancelEdit = () => { setEditingId(null); setForm({ question_text: '', answer: '', category: 'Publisher Info' }); };

  const filtered = answers.filter(a => !search || a.question_text.toLowerCase().includes(search.toLowerCase()) || a.answer.toLowerCase().includes(search.toLowerCase()));
  const grouped = ANSWER_CATEGORIES.reduce((acc, cat) => { acc[cat] = filtered.filter(a => (a.category || 'Other') === cat); return acc; }, {});

  const AnswerForm = ({ isNew }) => (
    <div className="space-y-3 bg-zinc-800/50 rounded-lg p-4 border border-zinc-700">
      <div>
        <label className="block text-xs text-zinc-400 mb-1">Category</label>
        <select value={form.category} onChange={e => setForm({ ...form, category: e.target.value })}
          className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm">
          {ANSWER_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>
      <div>
        <label className="block text-xs text-zinc-400 mb-1">Question / Field Label</label>
        <input type="text" value={form.question_text} onChange={e => setForm({ ...form, question_text: e.target.value })}
          placeholder="e.g. Does your site accept DoubleVerify blocking tags?"
          className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm"/>
      </div>
      <div>
        <label className="block text-xs text-zinc-400 mb-1">Your Answer</label>
        <textarea value={form.answer} onChange={e => setForm({ ...form, answer: e.target.value })} rows={3}
          placeholder="e.g. No — we accept DV monitoring only"
          className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm resize-none"/>
      </div>
      <div className="flex gap-2">
        <button onClick={() => saveAnswer(isNew)} disabled={saving || !form.question_text || !form.answer}
          className="bg-nextide-turquoise text-black px-4 py-1.5 rounded-lg text-sm font-semibold disabled:opacity-50">
          {saving ? 'Saving...' : 'Save'}
        </button>
        <button onClick={() => { cancelEdit(); setShowAdd(false); }} className="bg-zinc-700 text-white px-4 py-1.5 rounded-lg text-sm">Cancel</button>
      </div>
    </div>
  );

  if (loading) return <div className="text-zinc-500 text-xs">Loading...</div>;

  return (
    <div>
      <div className="flex items-center gap-3 mb-4">
        <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search questions..."
          className="bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm flex-1"/>
        <button onClick={() => { setShowAdd(!showAdd); setEditingId(null); setForm({ question_text: '', answer: '', category: 'Publisher Info' }); }}
          className="bg-nextide-turquoise text-black px-4 py-2 rounded-lg text-sm font-semibold">+ Add</button>
      </div>
      {showAdd && <div className="mb-4"><AnswerForm isNew /></div>}
      {answers.length === 0 && !showAdd ? (
        <div className="text-center py-6 text-zinc-500 text-xs">No answers yet — add your standard RFP responses above.</div>
      ) : (
        <div className="space-y-4">
          {ANSWER_CATEGORIES.map(cat => {
            const catAnswers = grouped[cat];
            if (!catAnswers || catAnswers.length === 0) return null;
            return (
              <div key={cat}>
                <div className="text-[10px] font-semibold text-zinc-500 uppercase tracking-wide mb-2">{cat}</div>
                <div className="space-y-2">
                  {catAnswers.map(a => (
                    <div key={a.id}>
                      {editingId === a.id ? <AnswerForm isNew={false} /> : (
                        <div className="flex items-start justify-between gap-4 p-3 bg-zinc-800/50 rounded-lg border border-zinc-800 group">
                          <div className="flex-1 min-w-0">
                            <div className="text-white text-xs font-medium">{a.question_text}</div>
                            <div className="text-zinc-400 text-xs mt-1">{a.answer}</div>
                          </div>
                          <div className="flex gap-2 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button onClick={() => startEdit(a)} className="text-zinc-400 hover:text-white text-xs px-2 py-1 bg-zinc-700 rounded">Edit</button>
                            <button onClick={() => deleteAnswer(a.id)} className="text-zinc-400 hover:text-red-400 text-xs px-2 py-1 bg-zinc-700 rounded">Delete</button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ============================================================
// SCREENSHOT LIBRARY SECTION
// ============================================================

const AD_UNIT_TYPES = ['OmniBanners', 'Immersive Frames', 'Chatbot', 'Panel', 'SpotReel', 'Sponsored Stream', 'Social Post', 'Other'];

const ScreenshotLibrarySection = () => {
  const [screenshots, setScreenshots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedUnit, setSelectedUnit] = useState('OmniBanners');
  const [labelInput, setLabelInput] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editLabel, setEditLabel] = useState('');

  useEffect(() => { fetchScreenshots(); }, []);

  const fetchScreenshots = async () => {
    try { const res = await api.get('/org/screenshot-library'); setScreenshots(res.data.screenshots || []); }
    catch (e) {} finally { setLoading(false); }
  };

  const uploadScreenshot = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('ad_unit_type', selectedUnit);
    formData.append('label', labelInput || file.name.replace(/\.[^.]+$/, ''));
    try {
      await api.post('/org/screenshot-library', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      await fetchScreenshots(); setLabelInput(''); e.target.value = '';
    } catch (err) { alert('Upload failed'); } finally { setUploading(false); }
  };

  const deleteScreenshot = async (id) => {
    if (!window.confirm('Delete this screenshot?')) return;
    try { await api.delete(`/org/screenshot-library/${id}`); setScreenshots(prev => prev.filter(s => s.id !== id)); }
    catch (e) { alert('Failed'); }
  };

  const saveLabel = async (id) => {
    try {
      await api.put(`/org/screenshot-library/${id}`, { label: editLabel });
      setScreenshots(prev => prev.map(s => s.id === id ? { ...s, label: editLabel } : s));
      setEditingId(null);
    } catch (e) { alert('Failed'); }
  };

  const grouped = AD_UNIT_TYPES.reduce((acc, unit) => { acc[unit] = screenshots.filter(s => s.ad_unit_type === unit); return acc; }, {});

  if (loading) return <div className="text-zinc-500 text-xs">Loading...</div>;

  return (
    <div>
      <div className="flex items-center gap-3 mb-5 flex-wrap">
        <select value={selectedUnit} onChange={e => setSelectedUnit(e.target.value)}
          className="bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm">
          {AD_UNIT_TYPES.map(u => <option key={u} value={u}>{u}</option>)}
        </select>
        <input type="text" value={labelInput} onChange={e => setLabelInput(e.target.value)} placeholder="Label (optional)"
          className="bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm w-40"/>
        <label className={`cursor-pointer px-4 py-2 rounded-lg text-sm font-semibold ${uploading ? 'bg-zinc-700 text-zinc-400' : 'bg-nextide-turquoise text-black'}`}>
          {uploading ? 'Uploading...' : '+ Upload'}
          <input type="file" accept="image/*" className="hidden" onChange={uploadScreenshot} disabled={uploading}/>
        </label>
      </div>
      {screenshots.length === 0 ? (
        <div className="text-center py-6 text-zinc-500 text-xs">No screenshots yet — upload examples of each ad unit type above.</div>
      ) : (
        <div className="space-y-5">
          {AD_UNIT_TYPES.map(unit => {
            const items = grouped[unit];
            if (!items || items.length === 0) return null;
            return (
              <div key={unit}>
                <div className="text-[10px] font-semibold text-zinc-500 uppercase tracking-wide mb-2">{unit}</div>
                <div className="grid grid-cols-3 gap-3">
                  {items.map(s => (
                    <div key={s.id} className="bg-zinc-800 border border-zinc-700 rounded-lg overflow-hidden group">
                      <div className="aspect-video bg-zinc-900 flex items-center justify-center overflow-hidden">
                        {s.imageData
                          ? <img src={s.imageData} alt={s.label} className="w-full h-full object-cover"/>
                          : <div className="w-full h-full flex items-center justify-center text-zinc-600 text-xs">No preview</div>
                        }
                      </div>
                      <div className="px-2 py-1.5">
                        {editingId === s.id ? (
                          <div className="flex gap-1">
                            <input autoFocus type="text" value={editLabel} onChange={e => setEditLabel(e.target.value)}
                              onKeyDown={e => { if (e.key === 'Enter') saveLabel(s.id); if (e.key === 'Escape') setEditingId(null); }}
                              className="flex-1 bg-zinc-700 border border-zinc-600 rounded px-1 py-0.5 text-white text-xs focus:outline-none"/>
                            <button onClick={() => saveLabel(s.id)} className="text-nextide-turquoise text-xs">✓</button>
                            <button onClick={() => setEditingId(null)} className="text-zinc-500 text-xs">✕</button>
                          </div>
                        ) : (
                          <div className="flex items-center justify-between">
                            <span className="text-white text-xs truncate">{s.label}</span>
                            <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity ml-1 shrink-0">
                              <button onClick={() => { setEditingId(s.id); setEditLabel(s.label); }} className="text-zinc-400 hover:text-white text-xs">✎</button>
                              <button onClick={() => deleteScreenshot(s.id)} className="text-zinc-400 hover:text-red-400 text-xs">×</button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
